import React from 'react';
import { X, Paperclip, Minus, Plus, RotateCcw, Download, Trash2 } from 'lucide-react';
import Base64Image from '../../components/ui/Base64Image';

interface AttachmentViewModalProps {
  viewingAttachment: any;
  imageZoom: number;
  language: 'en' | 'ar';
  getAttachmentTypeName: (type: string, language: 'en' | 'ar') => string;
  getAttachmentTypeIcon: (type: string) => string;
  getMimeTypeFromAttachmentType: (type: string) => string;
  formatDate: (timestamp: number) => string;
  onClose: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
  onDownloadAttachment: (attachment: any) => void;
  onDeleteAttachment: (attachmentId: string) => Promise<void>;
}

const AttachmentViewModal: React.FC<AttachmentViewModalProps> = ({
  viewingAttachment,
  imageZoom,
  language,
  getAttachmentTypeName,
  getAttachmentTypeIcon,
  getMimeTypeFromAttachmentType,
  formatDate,
  onClose,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onDownloadAttachment,
  onDeleteAttachment
}) => {
  const handleDeleteAttachment = async () => {
    if (window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا المرفق؟' : 'Are you sure you want to delete this attachment?')) {
      try {
        await onDeleteAttachment(viewingAttachment.id);
        onClose(); // Close the modal after successful deletion
      } catch (error) {
        console.error('Error deleting attachment:', error);
        // You could add a toast notification here if you have one
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2">
      <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-7xl max-h-[98vh] w-full relative flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Paperclip className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-slate-800 dark:text-white">
                {viewingAttachment.notes || (viewingAttachment.attachmentURL ? 'Image' : 'Document')}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {getAttachmentTypeName(viewingAttachment.type, language)} • {formatDate(Number(viewingAttachment.createdAt))}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Large Image/Preview Area */}
          <div className="flex-1 flex flex-col bg-slate-50 dark:bg-slate-900/50">
            {/* Zoom Controls */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
              <div className="flex items-center space-x-2">
                <button 
                  onClick={onZoomOut}
                  disabled={imageZoom <= 25}
                  className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={language === 'ar' ? 'تصغير' : 'Zoom Out'}
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400 min-w-[60px] text-center">
                  {imageZoom}%
                </span>
                <button
                  onClick={onZoomIn}
                  disabled={imageZoom >= 300}
                  className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={language === 'ar' ? 'تكبير' : 'Zoom In'}
                >
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  onClick={onResetZoom}
                  className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                  title={language === 'ar' ? 'إعادة تعيين التكبير' : 'Reset Zoom'}
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                {language === 'ar' ? 'استخدم عجلة الماوس للتحكم في التكبير' : 'Use mouse wheel to control zoom'}
              </div>
            </div>
            
            {/* Image Container */}
            <div 
              className="flex-1 flex items-center justify-center p-6 overflow-auto"
              onWheel={(e) => {
                e.preventDefault();
                if (e.deltaY < 0) {
                  onZoomIn();
                } else {
                  onZoomOut();
                }
              }}
            >
              {viewingAttachment.attachmentURL ? (
                <div 
                  className="relative"
                  style={{ transform: `scale(${imageZoom / 100})`, transformOrigin: 'center' }}
                >
                  <Base64Image
                    base64={viewingAttachment.attachmentURL}
                    mimeType={getMimeTypeFromAttachmentType(viewingAttachment.type)}
                    alt={viewingAttachment.notes || 'Attachment preview'}
                    className="max-w-none max-h-none object-contain rounded-lg shadow-lg"
                    style={{ 
                      width: 'auto', 
                      height: 'auto',
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                  />
                </div>
              ) : (
                <div className="w-64 h-64 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex flex-col items-center justify-center text-white">
                  <div className="text-6xl mb-4">
                    {getAttachmentTypeIcon(viewingAttachment.type)}
                  </div>
                  <p className="text-lg font-semibold">
                    {getAttachmentTypeName(viewingAttachment.type, language)}
                  </p>
                </div>
              )}
              
              {/* Fallback for failed images */}
              <div className="hidden w-64 h-64 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex flex-col items-center justify-center text-white">
                <div className="text-6xl mb-4">
                  {getAttachmentTypeIcon(viewingAttachment.type)}
                </div>
                <p className="text-lg font-semibold">
                  {getAttachmentTypeName(viewingAttachment.type, language)}
                </p>
              </div>
            </div>
          </div>

          {/* Attachment Details Sidebar */}
          <div className="w-96 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 p-6 overflow-y-auto">
            <h4 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
              {language === 'ar' ? 'تفاصيل المرفق' : 'Attachment Details'}
            </h4>
            
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {language === 'ar' ? 'الاسم' : 'Name'}
                  </label>
                  <p className="text-slate-800 dark:text-white mt-1">
                    {viewingAttachment.notes || (viewingAttachment.attachmentURL ? 'Image' : 'Document')}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {language === 'ar' ? 'النوع' : 'Type'}
                  </label>
                  <p className="text-slate-800 dark:text-white mt-1">
                    {getAttachmentTypeName(viewingAttachment.type, language)}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {language === 'ar' ? 'تاريخ الرفع' : 'Upload Date'}
                  </label>
                  <p className="text-slate-800 dark:text-white mt-1">
                    {formatDate(Number(viewingAttachment.createdAt))}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {language === 'ar' ? 'آخر تحديث' : 'Last Updated'}
                  </label>
                  <p className="text-slate-800 dark:text-white mt-1">
                    {formatDate(Number(viewingAttachment.updatedAt))}
                  </p>
                </div>
              </div>

              {/* System Info */}
              <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <h5 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  {language === 'ar' ? 'معلومات النظام' : 'System Information'}
                </h5>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">ID:</span>
                    <span className="text-slate-800 dark:text-white font-mono text-xs">
                      {viewingAttachment.id}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">
                      {language === 'ar' ? 'الحالة' : 'Status'}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      viewingAttachment.isArchived 
                        ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300'
                        : viewingAttachment.isDeleted
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                    }`}>
                      {viewingAttachment.isArchived 
                        ? (language === 'ar' ? 'مؤرشف' : 'Archived')
                        : viewingAttachment.isDeleted
                        ? (language === 'ar' ? 'محذوف' : 'Deleted')
                        : (language === 'ar' ? 'نشط' : 'Active')
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 border-t border-slate-200 dark:border-slate-700 space-y-3">
                <button 
                  onClick={() => onDownloadAttachment(viewingAttachment)}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>{language === 'ar' ? 'تحميل المرفق' : 'Download Attachment'}</span>
                </button>
                <button 
                  onClick={handleDeleteAttachment}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>{language === 'ar' ? 'حذف المرفق' : 'Delete Attachment'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachmentViewModal;
